import React, { useRef } from 'react';
import { clsx } from 'clsx';
import { LetterGrade, gradeToColor, isLetterGrade } from '../../utils/gradeEngine';

export type GradeStampSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface GradeStampProps {
  /** Letter grade, or anything else to render a neutral dash stamp. */
  grade: LetterGrade | string | null | undefined;
  size?: GradeStampSize;
  /** Play the stamp-in entrance on first mount. */
  animate?: boolean;
  /** Small caption under the stamp, e.g. "DRAFT". */
  label?: string;
  className?: string;
}

const SIZE_STYLES: Record<
  GradeStampSize,
  { box: string; text: string; border: string; label: string }
> = {
  xs: { box: 'w-8 h-8', text: 'text-sm', border: 'border-2', label: 'text-[9px]' },
  sm: { box: 'w-12 h-12', text: 'text-lg', border: 'border-2', label: 'text-[10px]' },
  md: { box: 'w-16 h-16', text: 'text-2xl', border: 'border-[3px]', label: 'text-xs' },
  lg: { box: 'w-24 h-24', text: 'text-4xl', border: 'border-4', label: 'text-xs' },
  xl: { box: 'w-32 h-32', text: 'text-6xl', border: 'border-4', label: 'text-sm' },
};

/**
 * The report-card grade stamp — a slightly rotated, ink-stamped letter grade.
 * The single core visual primitive of the redesign.
 */
const GradeStamp: React.FC<GradeStampProps> = ({
  grade,
  size = 'md',
  animate = false,
  label,
  className,
}) => {
  // Animate only on the very first mount of this instance, never on rerenders.
  const hasAnimated = useRef(false);
  const shouldAnimate = animate && !hasAnimated.current;
  hasAnimated.current = true;

  const valid = isLetterGrade(typeof grade === 'string' ? grade : undefined);
  const display = valid ? (grade as LetterGrade) : '—';
  const color = valid ? gradeToColor(grade as LetterGrade) : '#5A6378';
  const s = SIZE_STYLES[size];

  return (
    <div className={clsx('inline-flex flex-col items-center gap-1', className)}>
      <div
        className={clsx(
          'rounded-full flex items-center justify-center select-none',
          'font-mono font-bold -rotate-3',
          s.box,
          s.text,
          s.border,
          shouldAnimate && 'animate-stamp-in'
        )}
        style={{
          color,
          borderColor: color,
          backgroundColor: `${color}26`, // ~15% opacity fill
        }}
        role="img"
        aria-label={valid ? `Grade: ${display}` : 'Grade unavailable'}
      >
        {display}
      </div>
      {label && (
        <span
          className={clsx(
            'font-mono font-semibold uppercase tracking-widest text-text-secondary',
            s.label
          )}
        >
          {label}
        </span>
      )}
    </div>
  );
};

export default GradeStamp;
